import * as React from "react";
import * as __intl__ from "@frameio/i18n";
export type Text = {
    bullet: string;
    membershipFromGroup: (groupName?: string | null) => string | React.ReactNode;
    inheritedAccess: (resourceType: string) => string | React.ReactNode;
    labelRole: (memberName: string) => string | React.ReactNode;
    labelAuthedUser: string;
    otherMembership: (resourceName: string) => string | React.ReactNode;
    otherMemberships: (hasGroup: boolean) => string | React.ReactNode;
    guest: string;
    pending: string;
    resendInvitation: string;
    sent: string;
    removeMember: string;
    removeMemberTooltip: string;
};
export type Deps = {
    capitalize: any;
};
export default {
    DEFINE(_messages: Text, { capitalize }: Deps): void {
        return Object.defineProperties(this, {
            /** Bullet separator used in member information */
            bullet: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "InviteAccessMember.bullet?c=2hzq458et9ggq", defaultMessage: " \u2022 " }));
                },
                enumerable: true,
                configurable: true
            },
            /** Text indicating membership via a user group */
            membershipFromGroup: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => (groupName?: string | null) => __intl__.intl().formatMessage({ id: "InviteAccessMember.membershipFromGroup?c=2m3626eo2g74u", defaultMessage: "{groupName, select, true {via group \u2013 {groupName}} other {}}" }, { groupName: groupName }));
                },
                enumerable: true,
                configurable: true
            },
            /** Text indicating inherited access from a resource type */
            inheritedAccess: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => (resourceType: string) => __intl__.intl().formatMessage({ id: "InviteAccessMember.inheritedAccess?c=ysxsy2p1recj", defaultMessage: "Inherited from {var}" }, { var: capitalize(resourceType) }));
                },
                enumerable: true,
                configurable: true
            },
            /** Label for role selection dropdown */
            labelRole: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => (memberName: string) => __intl__.intl().formatMessage({ id: "InviteAccessMember.labelRole?c=33nmcnqkc6wyn", defaultMessage: "Change role for {memberName}" }, { memberName: memberName }));
                },
                enumerable: true,
                configurable: true
            },
            /** Label indicating current authenticated user */
            labelAuthedUser: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "InviteAccessMember.labelAuthedUser?c=33hg9nvaytt5x", defaultMessage: "(you)" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Text showing single additional membership via a resource */
            otherMembership: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => (resourceName: string) => __intl__.intl().formatMessage({ id: "InviteAccessMember.otherMembership?c=1m4rq9524m5pp", defaultMessage: "This user also has access via {resourceName}" }, { resourceName: resourceName }));
                },
                enumerable: true,
                configurable: true
            },
            /** Text showing multiple additional memberships */
            otherMemberships: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => (hasGroup: boolean) => __intl__.intl().formatMessage({ id: "InviteAccessMember.otherMemberships?c=3k5ahmdgtldv6", defaultMessage: "This user also has access via parent resources {hasGroup, select, true {or user groups} other {}}" }, { hasGroup: hasGroup }));
                },
                enumerable: true,
                configurable: true
            },
            /** Guest badge suffix for guest users */
            guest: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "InviteAccessMember.guest?c=zt491u1r2l2v", defaultMessage: " \u00B7 Guest" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Label for pending (not yet accepted invite) user */
            pending: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "InviteAccessMember.pending?c=1hdx1m8rhv786", defaultMessage: "Pending User" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Button text to resend invitation email */
            resendInvitation: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "InviteAccessMember.resendInvitation?c=33a7mfu3eeb6x", defaultMessage: "Resend invite" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Confirmation message shown after resending invite */
            sent: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "InviteAccessMember.sent?c=tnn0tw6mublx", defaultMessage: "Invitation sent!" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Menu item text to remove member access */
            removeMember: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "InviteAccessMember.removeMember?c=2p2nn96ea4b9x", defaultMessage: "Remove access" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Tooltip text for remove member button */
            removeMemberTooltip: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "InviteAccessMember.removeMemberTooltip?c=1e4edcwevlhse", defaultMessage: "Remove from folder" }));
                },
                enumerable: true,
                configurable: true
            }
        });
    }
} as Text & {
    DEFINE(messages: Text, deps: Deps): void;
};
